// The shell's own state (no Vuex): the sidebar, density, where the pane
// was, the badges and who holds the pane's foot. Reactive, so the sidebar,
// rail and panes read it directly.
import { reactive } from "vue";
import { apiGet } from "../services/api/client.js";
import { placeById, SESSION_PLACES } from "./places.js";

const COLLAPSE_KEY = "shell_sidebar_collapsed";

function readCollapsed() {
  try {
    return typeof window !== "undefined" && window.localStorage.getItem(COLLAPSE_KEY) === "1";
  } catch (e) {
    return false;
  }
}

export const shellState = reactive({
  // The desktop sidebar is folded to the rail.
  collapsed: readCollapsed(),
  density: "pointer",
  // Shortcut chips (⌘K, ⌘F) show only once a keyboard is in use; at
  // pointer density it is assumed (setDensity), at touch it waits for a
  // keydown (shortcuts.js).
  keyboardUsed: false,
  // The fullPath of the last route that isn't an overlay right now.
  lastPane: "/sessions/mine",
  // The sidebar's selection for lastPane; overlays and the full-pane
  // profile keep it.
  lastPlace: "mine",
  // mine, friends, live or liked: the list beside an open session.
  lastSessionPlace: "mine",
  // The Sessions header's text while it hands over to /search (3.6).
  searchDraft: "",
  badges: { inbox: 0 },
  // While above 0 the PlayerBar or MiniPlayer hides (a selection bar or
  // phone Discover holds the foot).
  footClaims: 0,
  // The upload queue's menu (UploadQueueButton).
  uploads: { open: false, owner: null },
});

export function setCollapsed(value) {
  shellState.collapsed = !!value;
  try {
    window.localStorage.setItem(COLLAPSE_KEY, value ? "1" : "0");
  } catch (e) {
    /* storage unavailable: it lasts this visit */
  }
}

export function setDensity(d) {
  shellState.density = d === "touch" ? "touch" : "pointer";
  if (shellState.density === "pointer") shellState.keyboardUsed = true;
}

// Hold the pane's foot (hide the player). Returns the release function.
export function claimFoot() {
  shellState.footClaims += 1;
  let released = false;
  return () => {
    if (released) return;
    released = true;
    releaseFoot();
  };
}

export function releaseFoot() {
  shellState.footClaims = Math.max(0, shellState.footClaims - 1);
}

// The Inbox badge: pending invitations only (SI-24), at
// data.results.count.
let badgeSeq = 0;
export async function refreshInboxBadge() {
  const seq = ++badgeSeq;
  try {
    const res = await apiGet("/invitations/inbox/count");
    if (seq !== badgeSeq) return shellState.badges.inbox;
    const n = Number(res && res.count);
    shellState.badges.inbox = Number.isFinite(n) && n > 0 ? n : 0;
  } catch (e) {
    /* keep what it showed */
  }
  return shellState.badges.inbox;
}

/**
 * The overlay a route is right now, or null when it renders as a page:
 * Inbox is always a sheet; Settings is the window only at pointer density
 * with a window of 700 px or more (SI-9), else the pushed page.
 */
export function overlayKind(route, { density = shellState.density, width = 1280 } = {}) {
  const meta = (route && route.meta) || {};
  if (meta.overlay === "inbox") return "inbox";
  if (meta.overlay === "settings" && density === "pointer" && width >= 700) return "settings";
  return null;
}

/**
 * The shell mode a route takes (3.1): "pane", "session" (the list column
 * beside it), "full" (no sidebar or rail) or "bare".
 */
export function shellMode(route, { density = shellState.density, width = 1280 } = {}) {
  const meta = (route && route.meta) || {};
  const shell = meta.shell || "pane";
  if (shell === "bare") return "bare";
  if (shell === "overlay") return overlayKind(route, { density, width }) ? "overlay" : "full";
  if (shell === "session") {
    if (width >= 1100 && density === "pointer") return "session";
    return width >= 700 ? "full" : "pane";
  }
  return shell === "full" ? "full" : "pane";
}

/**
 * The session header's way back (Session.vue; SI-2, SI-27): "chevron" for
 * the phone's "‹" and a full-width pointer session's "Leave session",
 * "sidebar" for a full-width touch session's "Show sidebar", else null.
 * None beside the list column ("session" mode), however narrow the session
 * is there: the columns navigate (status_bar.dart draws no back button
 * when they show). None signed out or outside the shell.
 */
export function sessionBackKind({ inShell = false, signedIn = false, mode = null, touch = false, narrow = false } = {}) {
  if (!inShell || !signedIn) return null;
  if (mode === "full") return touch ? "sidebar" : "chevron";
  if (mode === "session") return null;
  return narrow ? "chevron" : null;
}

// Remember where the pane is, for overlays and the back targets.
export function recordPane(route, { overlay = false } = {}) {
  // Not before the first navigation lands (START_LOCATION matches nothing),
  // and never a page outside the shell's panes (Welcome).
  if (!route || overlay || !route.matched || !route.matched.length) return;
  if (route.meta && route.meta.shell === "bare") return;
  shellState.lastPane = route.fullPath || route.path || shellState.lastPane;
  const place = route.meta && route.meta.place;
  if (place === "@session") {
    shellState.lastPlace = shellState.lastSessionPlace;
  } else if (place && placeById(place)) {
    shellState.lastPlace = place;
    if (SESSION_PLACES.includes(place)) shellState.lastSessionPlace = place;
  }
}

// The list an open session returns to.
export function lastSessionListPath() {
  const p = placeById(shellState.lastSessionPlace);
  return p ? p.path : "/sessions/mine";
}

// An upload still on its way (the queue's ring and App.vue's watchers).
export const ACTIVE_UPLOAD_STATES = ["added", "processing", "processed", "uploading"];
